import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Search, Users, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ClientesAdminPage() {
  const { getAuthHeader, user } = useAuth();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '', telefono: '', direccion: '', colonia: '', comentarios: '',
  });

  const isAdmin = user?.rol === 'admin';

  const fetchClientes = async () => {
    try {
      const response = await axios.get(`${API_URL}/clientes`, { headers: getAuthHeader() });
      setClientes(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClientes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenDialog = (cliente = null) => {
    if (cliente) {
      setEditingCliente(cliente);
      setFormData({ nombre: cliente.nombre, telefono: cliente.telefono, direccion: cliente.direccion || '', colonia: cliente.colonia || '', comentarios: cliente.comentarios || '' });
    } else {
      setEditingCliente(null);
      setFormData({ nombre: '', telefono: '', direccion: '', colonia: '', comentarios: '' });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCliente) {
        await axios.put(`${API_URL}/clientes/${editingCliente.id}`, formData, { headers: getAuthHeader() });
        toast.success('Cliente actualizado');
      } else {
        await axios.post(`${API_URL}/clientes`, formData, { headers: getAuthHeader() });
        toast.success('Cliente creado');
      }
      setDialogOpen(false);
      fetchClientes();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este cliente?')) return;
    try {
      await axios.delete(`${API_URL}/clientes/${id}`, { headers: getAuthHeader() });
      toast.success('Cliente eliminado');
      fetchClientes();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Error al eliminar');
    }
  };

  const filteredClientes = clientes.filter(c =>
    c.nombre.toLowerCase().includes(searchTerm.toLowerCase()) || c.telefono.includes(searchTerm)
  );

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="clientes-admin-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-3xl font-bold text-allegra-dark">Clientes</h1>
          <p className="text-xs sm:text-sm text-allegra-gray">Gestiona tu base de clientes</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="rounded-full gap-1.5 h-9 text-xs sm:text-sm sm:h-10" data-testid="add-cliente-btn">
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Nuevo Cliente</span>
          <span className="sm:hidden">Nuevo</span>
        </Button>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2 sm:pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-allegra-gray" />
            <Input
              placeholder="Buscar por nombre o telefono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 rounded-xl text-sm"
              data-testid="search-clientes"
            />
          </div>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
            </div>
          ) : filteredClientes.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-10 h-10 text-allegra-gray/50 mx-auto mb-3" />
              <p className="text-allegra-gray text-sm">No hay clientes registrados</p>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden space-y-2">
                {filteredClientes.map((cliente) => (
                  <div key={cliente.id} className="p-3 bg-muted/50 rounded-xl" data-testid={`cliente-card-${cliente.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary font-bold text-sm">{cliente.nombre.charAt(0)}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-allegra-dark truncate">{cliente.nombre}</p>
                          <div className="flex items-center gap-1 text-xs text-allegra-gray">
                            <Phone className="w-3 h-3" />
                            {cliente.telefono}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleOpenDialog(cliente)} data-testid={`edit-cliente-${cliente.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {isAdmin && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDelete(cliente.id)} data-testid={`delete-cliente-${cliente.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {(cliente.direccion || cliente.colonia) && (
                      <div className="flex items-center gap-1 mt-1.5 ml-11 text-xs text-allegra-gray">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{[cliente.direccion, cliente.colonia].filter(Boolean).join(', ')}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Nombre</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Telefono</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Direccion</th>
                      <th className="text-left py-3 px-3 text-sm font-medium text-allegra-gray">Colonia</th>
                      <th className="text-right py-3 px-3 text-sm font-medium text-allegra-gray">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClientes.map((cliente) => (
                      <tr key={cliente.id} className="border-b last:border-0" data-testid={`cliente-row-${cliente.id}`}>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-primary font-bold text-sm">{cliente.nombre.charAt(0)}</span>
                            </div>
                            <span className="font-medium text-sm">{cliente.nombre}</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-sm text-allegra-gray">{cliente.telefono}</td>
                        <td className="py-3 px-3 text-sm text-allegra-gray max-w-[200px] truncate">{cliente.direccion || '-'}</td>
                        <td className="py-3 px-3 text-sm text-allegra-gray">{cliente.colonia || '-'}</td>
                        <td className="py-3 px-3 text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleOpenDialog(cliente)} data-testid={`edit-cliente-${cliente.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {isAdmin && (
                              <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(cliente.id)} data-testid={`delete-cliente-${cliente.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingCliente ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
            <DialogDescription>{editingCliente ? 'Modifica los datos del cliente' : 'Registra un nuevo cliente'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} placeholder="Nombre completo" required data-testid="cliente-nombre" />
            </div>
            <div className="space-y-2">
              <Label>Telefono *</Label>
              <Input value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} placeholder="55 1234 5678" required data-testid="cliente-telefono" />
            </div>
            <div className="space-y-2">
              <Label>Direccion</Label>
              <Input value={formData.direccion} onChange={(e) => setFormData({ ...formData, direccion: e.target.value })} placeholder="Calle y numero" data-testid="cliente-direccion" />
            </div>
            <div className="space-y-2">
              <Label>Colonia</Label>
              <Input value={formData.colonia} onChange={(e) => setFormData({ ...formData, colonia: e.target.value })} placeholder="Colonia" />
            </div>
            <div className="space-y-2">
              <Label>Comentarios</Label>
              <Textarea value={formData.comentarios} onChange={(e) => setFormData({ ...formData, comentarios: e.target.value })} placeholder="Notas sobre el cliente..." rows={3} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-cliente-btn">{editingCliente ? 'Guardar' : 'Crear'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
